import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import DomioLogo from '../components/common/DomioLogo';
import { Mail, AlertCircle, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useI18n } from '../i18n';
import { toast } from 'sonner';
import { validateEmailAddress, formatApiErrorDetail } from '../lib/emailValidation';

export const ForgotPasswordPage = () => {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const check = validateEmailAddress(email);
    if (!check.valid) {
      setEmailError(check.reason === 'required' ? t('auth.emailRequired') : t('auth.invalidEmail'));
      return;
    }
    setEmailError('');
    setLoading(true);
    setError('');
    try {
      await authAPI.forgotPassword(check.email);
      setSent(true);
      toast.success(t('auth.resetEmailSent'));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail, t('auth.resetRequestFailed'));
      setError(msg);
      if (err.response?.status === 429) {
        toast.error(msg);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center gap-2 mb-6">
          <DomioLogo size="lg" showText textClassName="text-slate-900" />
        </div>

        <Link to="/login" className="inline-flex items-center text-sm text-domio-600 hover:text-domio-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('auth.backToLogin')}
        </Link>

        <h1 className="font-manrope text-2xl font-bold text-slate-900 mb-2">{t('auth.forgotPasswordTitle')}</h1>
        <p className="text-slate-500 text-sm mb-6">{t('auth.forgotPasswordSubtitle')}</p>

        {sent ? (
          <div className="text-center py-4">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <p className="text-slate-600 text-sm">{t('auth.resetEmailSentDetail')}</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" /> {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    onBlur={() => {
                      const check = validateEmailAddress(email);
                      if (!check.valid && email.trim()) {
                        setEmailError(check.reason === 'required' ? t('auth.emailRequired') : t('auth.invalidEmail'));
                      }
                    }}
                    placeholder={t('auth.enterEmail')}
                    className={`pl-10 ${emailError ? 'border-red-400' : ''}`}
                    aria-invalid={Boolean(emailError)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                {emailError && <p className="text-sm text-red-600" role="alert">{emailError}</p>}
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-domio-600 hover:bg-domio-700 text-white"
                data-testid="forgot-password-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('auth.sendResetLink')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
